import React, { useState, useEffect, useMemo } from 'react';
import {
  Users,
  UserPlus,
  Clock,
  Mic,
  Film,
  Layers,
  Search,
  CheckCircle2,
  X,
  Edit3,
  ArrowRightLeft,
  Key,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  AlertTriangle,
  Lock
} from 'lucide-react';
import {
  InspectUser,
  UserDetail,
  fetchInspectUsers,
  fetchUserDetails,
  createInspectUser,
  updateInspectUser,
  reassignClips,
  changeUserPassword,
  banUser,
  unbanUser,
  deleteUser
} from '../../services/inspectService';
import { CharacterAvatar } from '../common/CharacterAvatar';

interface UsersTabProps {
  onRefreshData?: () => void;
}

export const UsersTab: React.FC<UsersTabProps> = ({ onRefreshData }) => {
  const [users, setUsers] = useState<InspectUser[]>([]);
  const [unregisteredNames, setUnregisteredNames] = useState<Array<{ user_name: string; count: number }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Detail drawer
  const [selectedUsername, setSelectedUsername] = useState<string | null>(null);
  const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailTab, setDetailTab] = useState<'clips' | 'takes' | 'entries' | 'friends'>('clips');

  // Create User Modal
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newAvatar, setNewAvatar] = useState('char-maverick');
  const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit User Modal
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [editAvatar, setEditAvatar] = useState('');
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  // Reassign Clips Modal
  const [isReassignOpen, setIsReassignOpen] = useState(false);
  const [targetUsername, setTargetUsername] = useState('');
  const [selectedClipIds, setSelectedClipIds] = useState<string[]>([]);
  const [isReassigning, setIsReassigning] = useState(false);

  // Change Password Modal
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [passwordTargetUser, setPasswordTargetUser] = useState<string | null>(null);
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [passwordStatusMsg, setPasswordStatusMsg] = useState<{ isError: boolean; text: string } | null>(null);

  // Ban User Modal
  const [isBanOpen, setIsBanOpen] = useState(false);
  const [banTargetUser, setBanTargetUser] = useState<string | null>(null);
  const [banDays, setBanDays] = useState(30);
  const [banReason, setBanReason] = useState('');
  const [isSubmittingBan, setIsSubmittingBan] = useState(false);
  const [banError, setBanError] = useState<string | null>(null);

  // Delete User Modal
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTargetUser, setDeleteTargetUser] = useState<string | null>(null);
  const [deletePurgeAssets, setDeletePurgeAssets] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Load performers list on mount
  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await fetchInspectUsers();
      if (data) {
        setUsers(data.users);
        setUnregisteredNames(data.unregisteredNames);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Filter users in memory (0 D1 row reads while searching)
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.trim().toLowerCase();
    return users.filter(u =>
      u.username.toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  // Aggregate stats
  const totalTakes = useMemo(() => users.reduce((acc, u) => acc + (u.takesCount || 0), 0), [users]);
  const totalDuration = useMemo(() => users.reduce((acc, u) => acc + (u.totalDuration || 0), 0), [users]);
  const totalEntries = useMemo(() => users.reduce((acc, u) => acc + (u.entriesCount || 0), 0), [users]);

  const handleSelectUser = async (username: string) => {
    setSelectedUsername(username);
    setIsLoadingDetail(true);
    setDetailTab('clips');
    setSelectedClipIds([]);
    try {
      const detail = await fetchUserDetails(username);
      setUserDetail(detail);
      if (detail?.user) {
        setEditEmail(detail.user.email || '');
        setEditAvatar(detail.user.avatarUrl || 'char-maverick');
      }
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername.trim()) return;
    setIsSubmittingCreate(true);
    setCreateError(null);
    try {
      const res = await createInspectUser({
        username: newUsername.trim(),
        email: newEmail.trim() || undefined,
        avatarUrl: newAvatar
      });
      if (res.success) {
        setIsCreateOpen(false);
        setNewUsername('');
        setNewEmail('');
        await loadUsers();
        if (onRefreshData) onRefreshData();
      } else {
        setCreateError(res.error || 'Failed to create performer');
      }
    } finally {
      setIsSubmittingCreate(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUsername) return;
    setIsSubmittingEdit(true);
    try {
      const res = await updateInspectUser({
        username: selectedUsername,
        email: editEmail.trim(),
        avatarUrl: editAvatar
      });
      if (res.success) {
        setIsEditOpen(false);
        await handleSelectUser(selectedUsername);
        await loadUsers();
      }
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleReassignClips = async () => {
    if (!targetUsername.trim() || selectedClipIds.length === 0) return;
    setIsReassigning(true);
    try {
      const res = await reassignClips({
        targetUser: targetUsername.trim(),
        clipIds: selectedClipIds
      });
      if (res.success) {
        setIsReassignOpen(false);
        setSelectedClipIds([]);
        if (selectedUsername) await handleSelectUser(selectedUsername);
        await loadUsers();
        if (onRefreshData) onRefreshData();
      }
    } finally {
      setIsReassigning(false);
    }
  };

  // Change Password Handler
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordTargetUser) return;
    setIsSubmittingPassword(true);
    setPasswordStatusMsg(null);
    try {
      const res = await changeUserPassword(passwordTargetUser, newPasswordValue);
      if (res.success) {
        setPasswordStatusMsg({ isError: false, text: res.message || 'Password changed successfully!' });
        setTimeout(() => {
          setIsPasswordOpen(false);
          setPasswordStatusMsg(null);
          setNewPasswordValue('');
        }, 1200);
      } else {
        setPasswordStatusMsg({ isError: true, text: res.error || 'Failed to update password' });
      }
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  // Ban User Handler
  const handleBanUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!banTargetUser) return;
    setIsSubmittingBan(true);
    setBanError(null);
    try {
      const res = await banUser(banTargetUser, banDays, banReason.trim());
      if (res.success) {
        setIsBanOpen(false);
        setBanReason('');
        await loadUsers();
        if (selectedUsername === banTargetUser) {
          await handleSelectUser(banTargetUser);
        }
      } else {
        setBanError(res.error || 'Failed to ban performer');
      }
    } finally {
      setIsSubmittingBan(false);
    }
  };

  // Quick Unban Handler
  const handleQuickUnban = async (username: string) => {
    try {
      const res = await unbanUser(username);
      if (res.success) {
        await loadUsers();
        if (selectedUsername === username) {
          await handleSelectUser(username);
        }
      }
    } catch (err: any) {
      console.error('Failed to unban user:', err);
    }
  };

  // Delete User Handler
  const handleDeleteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deleteTargetUser) return;
    if (deleteConfirmationText.trim().toLowerCase() !== deleteTargetUser.toLowerCase()) {
      setDeleteError(`Please type "${deleteTargetUser}" exactly to confirm.`);
      return;
    }
    setIsSubmittingDelete(true);
    setDeleteError(null);
    try {
      const res = await deleteUser(deleteTargetUser, deletePurgeAssets);
      if (res.success) {
        setIsDeleteOpen(false);
        setDeleteConfirmationText('');
        if (selectedUsername === deleteTargetUser) {
          setSelectedUsername(null);
          setUserDetail(null);
        }
        await loadUsers();
        if (onRefreshData) onRefreshData();
      } else {
        setDeleteError(res.error || 'Failed to delete user');
      }
    } finally {
      setIsSubmittingDelete(false);
    }
  };

  const formatSeconds = (sec: number): string => {
    if (sec < 60) return `${Math.round(sec)}s`;
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return `${m}m ${s}s`;
  };

  const formatTimestamp = (ts: number | null): string => {
    if (!ts) return 'Never';
    const date = new Date(ts * 1000);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const nowSec = Math.floor(Date.now() / 1000);

  return (
    <div>
      {/* Metrics Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={cardStyle}>
          <div style={{ padding: '9px', borderRadius: 'var(--radius-md)', background: 'rgba(6, 182, 212, 0.15)', color: 'var(--accent-cyan)' }}>
            <Users size={20} />
          </div>
          <div>
            <div style={metricValueStyle}>{users.length}</div>
            <div style={metricLabelStyle}>Registered Performers</div>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ padding: '9px', borderRadius: 'var(--radius-md)', background: 'rgba(255, 107, 107, 0.15)', color: 'var(--accent-coral)' }}>
            <Mic size={20} />
          </div>
          <div>
            <div style={metricValueStyle}>{totalTakes}</div>
            <div style={metricLabelStyle}>Total Takes Dubbed</div>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ padding: '9px', borderRadius: 'var(--radius-md)', background: 'rgba(16, 185, 129, 0.15)', color: 'var(--accent-emerald)' }}>
            <Clock size={20} />
          </div>
          <div>
            <div style={metricValueStyle}>{formatSeconds(totalDuration)}</div>
            <div style={metricLabelStyle}>Total Dubbed Speech</div>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ padding: '9px', borderRadius: 'var(--radius-md)', background: 'rgba(139, 92, 246, 0.15)', color: 'var(--accent-purple)' }}>
            <Layers size={20} />
          </div>
          <div>
            <div style={metricValueStyle}>{totalEntries}</div>
            <div style={metricLabelStyle}>SoulSync Entries</div>
          </div>
        </div>
      </div>

      {/* Unregistered Creator Notice */}
      {unregisteredNames.length > 0 && (
        <div style={{
          padding: '12px 16px',
          borderRadius: 'var(--radius-md)',
          background: 'rgba(245, 158, 11, 0.12)',
          border: '1px solid var(--accent-amber)',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <div>
            <span style={{ color: 'var(--accent-amber)', fontWeight: 700, fontSize: '0.85rem' }}>
              Notice: Unregistered creators found in base_clips:
            </span>{' '}
            <span style={{ color: 'var(--text-primary)', fontSize: '0.82rem' }}>
              {unregisteredNames.map(u => `${u.user_name} (${u.count} clips)`).join(', ')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              if (unregisteredNames[0]) {
                setNewUsername(unregisteredNames[0].user_name);
                setIsCreateOpen(true);
              }
            }}
            style={{
              padding: '5px 12px',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'var(--accent-amber)',
              color: '#000',
              fontWeight: 700,
              fontSize: '0.78rem',
              cursor: 'pointer'
            }}
          >
            Register Creator Profile
          </button>
        </div>
      )}

      {/* Action Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        marginBottom: '1.25rem',
        flexWrap: 'wrap'
      }}>
        {/* Search */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-glass)',
          background: 'var(--bg-glass)',
          flex: '1 1 260px',
          maxWidth: '380px'
        }}>
          <Search size={15} color="var(--text-muted)" />
          <input
            type="text"
            placeholder="Search performers by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#fff',
              outline: 'none',
              fontSize: '0.85rem',
              width: '100%'
            }}
          />
        </div>

        {/* Create Performer Button */}
        <button
          type="button"
          onClick={() => setIsCreateOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '9px 18px',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: 'linear-gradient(135deg, var(--accent-purple) 0%, var(--accent-coral) 100%)',
            color: 'white',
            fontWeight: 700,
            fontSize: '0.85rem',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)'
          }}
        >
          <UserPlus size={16} />
          <span>Add New Performer</span>
        </button>
      </div>

      {/* Users Table */}
      <div style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-glass)',
        background: 'var(--bg-card)',
        overflow: 'hidden'
      }}>
        {isLoading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <p>Loading performers list...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <p>No performers found.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: 'rgba(0,0,0,0.25)' }}>
                  <th style={thStyle}>Performer</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Tokens</th>
                  <th style={thStyle}>Tier</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Takes</th>
                  <th style={thStyle}>Speech Time</th>
                  <th style={thStyle}>Entries</th>
                  <th style={thStyle}>Clips</th>
                  <th style={thStyle}>Last Active</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u, idx) => {
                  const isBanned = Boolean(u.bannedUntil && u.bannedUntil > nowSec);
                  const daysLeft = isBanned ? Math.max(1, Math.ceil((u.bannedUntil! - nowSec) / 86400)) : 0;

                  return (
                    <tr
                      key={u.id}
                      onClick={() => handleSelectUser(u.username)}
                      style={{
                        cursor: 'pointer',
                        borderTop: idx > 0 ? '1px solid var(--border-glass)' : 'none',
                        background: selectedUsername === u.username
                          ? 'rgba(139, 92, 246, 0.12)'
                          : isBanned
                            ? 'rgba(239, 68, 68, 0.04)'
                            : 'transparent',
                        transition: 'background var(--transition-fast)'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedUsername !== u.username) e.currentTarget.style.background = 'var(--bg-card-hover)';
                      }}
                      onMouseLeave={(e) => {
                        if (selectedUsername !== u.username) {
                          e.currentTarget.style.background = isBanned ? 'rgba(239, 68, 68, 0.04)' : 'transparent';
                        }
                      }}
                    >
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <CharacterAvatar avatarId={u.avatarUrl || 'char-maverick'} size="sm" fallbackInitial={u.username.charAt(0)} />
                          <div>
                            <div style={{ fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>{u.username}</span>
                            </div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{u.id}</div>
                          </div>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        {isBanned ? (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '3px 8px',
                            borderRadius: 'var(--radius-sm)',
                            background: 'rgba(239, 68, 68, 0.2)',
                            border: '1px solid rgba(239, 68, 68, 0.4)',
                            color: '#fca5a5',
                            fontSize: '0.72rem',
                            fontWeight: 700
                          }}>
                            <ShieldAlert size={12} />
                            <span>BANNED ({daysLeft}d)</span>
                          </span>
                        ) : (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '3px 8px',
                            borderRadius: 'var(--radius-sm)',
                            background: 'rgba(16, 185, 129, 0.15)',
                            color: 'var(--accent-emerald)',
                            fontSize: '0.72rem',
                            fontWeight: 600
                          }}>
                            <CheckCircle2 size={12} />
                            <span>Active</span>
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)' }}>
                        <span style={{
                          color: (u.tokensBalance ?? 0) > 0 ? '#fbbf24' : 'var(--text-muted)',
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px'
                        }}>
                          🪙 {(u.tokensBalance ?? 0).toLocaleString()}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {u.subscriptionTier === 'creator_pro' ? (
                          <span style={{
                            background: 'rgba(234, 179, 8, 0.2)',
                            border: '1px solid #fbbf24',
                            color: '#fbbf24',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 700
                          }}>
                            PRO
                          </span>
                        ) : u.subscriptionTier === 'studio' ? (
                          <span style={{
                            background: 'rgba(168, 85, 247, 0.2)',
                            border: '1px solid #c084fc',
                            color: '#c084fc',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 700
                          }}>
                            STUDIO
                          </span>
                        ) : (
                          <span style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            color: '#94a3b8',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '0.7rem'
                          }}>
                            FREE
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: u.email ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
                        {u.email || '—'}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 700, color: 'var(--accent-coral)' }}>{u.takesCount || 0}</span>
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--accent-emerald)', fontWeight: 600 }}>
                        {formatSeconds(u.totalDuration || 0)}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{u.entriesCount || 0}</span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{u.clipsCount || 0}</span>
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                        {formatTimestamp(u.lastActiveAt)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            title="Manage Profile & Assets"
                            onClick={() => handleSelectUser(u.username)}
                            style={{
                              padding: '4px 10px',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--border-glass)',
                              background: 'transparent',
                              color: 'var(--accent-purple)',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}
                          >
                            Manage
                          </button>

                          <button
                            type="button"
                            title="Change Password"
                            onClick={() => {
                              setPasswordTargetUser(u.username);
                              setNewPasswordValue('');
                              setPasswordStatusMsg(null);
                              setIsPasswordOpen(true);
                            }}
                            style={{
                              padding: '5px',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--border-glass)',
                              background: 'var(--bg-glass)',
                              color: 'var(--text-secondary)',
                              cursor: 'pointer'
                            }}
                          >
                            <Key size={13} />
                          </button>

                          {isBanned ? (
                            <button
                              type="button"
                              title="Unban Account"
                              onClick={() => handleQuickUnban(u.username)}
                              style={{
                                padding: '5px',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid rgba(16, 185, 129, 0.4)',
                                background: 'rgba(16, 185, 129, 0.15)',
                                color: 'var(--accent-emerald)',
                                cursor: 'pointer'
                              }}
                            >
                              <ShieldCheck size={13} />
                            </button>
                          ) : (
                            <button
                              type="button"
                              title="Ban Performer for 30 Days"
                              onClick={() => {
                                setBanTargetUser(u.username);
                                setBanDays(30);
                                setBanReason('');
                                setBanError(null);
                                setIsBanOpen(true);
                              }}
                              style={{
                                padding: '5px',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--border-glass)',
                                background: 'var(--bg-glass)',
                                color: 'var(--accent-coral)',
                                cursor: 'pointer'
                              }}
                            >
                              <ShieldAlert size={13} />
                            </button>
                          )}

                          <button
                            type="button"
                            title="Delete Performer"
                            onClick={() => {
                              setDeleteTargetUser(u.username);
                              setDeleteConfirmationText('');
                              setDeletePurgeAssets(false);
                              setDeleteError(null);
                              setIsDeleteOpen(true);
                            }}
                            style={{
                              padding: '5px',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--border-glass)',
                              background: 'var(--bg-glass)',
                              color: 'rgba(239, 68, 68, 0.75)',
                              cursor: 'pointer'
                            }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Detail Side Drawer */}
      {selectedUsername && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '640px',
          maxWidth: '92vw',
          height: '100vh',
          background: 'var(--bg-deep)',
          borderLeft: '1px solid var(--border-glass)',
          boxShadow: '-10px 0 45px rgba(0,0,0,0.6)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideIn 0.2s ease-out'
        }}>
          {/* Drawer Header */}
          <div style={{
            padding: '1.25rem',
            borderBottom: '1px solid var(--border-glass)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <CharacterAvatar
                avatarId={userDetail?.user?.avatarUrl || 'char-maverick'}
                size="md"
                fallbackInitial={selectedUsername.charAt(0)}
              />
              <div>
                <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff' }}>
                  {selectedUsername}
                </h2>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {userDetail?.user?.email || 'No email registered'}
                </div>
                {userDetail?.user?.bannedUntil && userDetail.user.bannedUntil > nowSec ? (
                  <div style={{
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: '#fca5a5',
                    fontSize: '0.72rem'
                  }}>
                    <ShieldAlert size={12} color="var(--accent-coral)" />
                    <span>
                      Suspended until {new Date(userDetail.user.bannedUntil * 1000).toLocaleDateString()}
                      {userDetail.user.bannedReason ? ` ("${userDetail.user.bannedReason}")` : ''}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => {
                  setPasswordTargetUser(selectedUsername);
                  setNewPasswordValue('');
                  setPasswordStatusMsg(null);
                  setIsPasswordOpen(true);
                }}
                title="Change Password"
                style={{
                  padding: '6px 10px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-glass)',
                  background: 'var(--bg-glass)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '0.76rem'
                }}
              >
                <Key size={12} />
                <span>Password</span>
              </button>

              {userDetail?.user?.bannedUntil && userDetail.user.bannedUntil > nowSec ? (
                <button
                  type="button"
                  onClick={() => handleQuickUnban(selectedUsername)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid rgba(16, 185, 129, 0.4)',
                    background: 'rgba(16, 185, 129, 0.15)',
                    color: 'var(--accent-emerald)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '0.76rem',
                    fontWeight: 700
                  }}
                >
                  <ShieldCheck size={12} />
                  <span>Unban</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setBanTargetUser(selectedUsername);
                    setBanDays(30);
                    setBanReason('');
                    setBanError(null);
                    setIsBanOpen(true);
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid rgba(239, 68, 68, 0.4)',
                    background: 'rgba(239, 68, 68, 0.15)',
                    color: '#fca5a5',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '0.76rem',
                    fontWeight: 700
                  }}
                >
                  <ShieldAlert size={12} />
                  <span>Ban 30d</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setDeleteTargetUser(selectedUsername);
                  setDeleteConfirmationText('');
                  setDeletePurgeAssets(false);
                  setDeleteError(null);
                  setIsDeleteOpen(true);
                }}
                title="Delete Performer"
                style={{
                  padding: '6px 10px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  background: 'transparent',
                  color: 'var(--accent-coral)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '0.76rem'
                }}
              >
                <Trash2 size={12} />
                <span>Delete</span>
              </button>

              <button
                type="button"
                onClick={() => setIsEditOpen(true)}
                title="Edit Performer Profile"
                style={{
                  padding: '6px 10px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-glass)',
                  background: 'var(--bg-glass)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '0.76rem'
                }}
              >
                <Edit3 size={12} />
                <span>Edit</span>
              </button>

              <button
                type="button"
                onClick={() => setSelectedUsername(null)}
                style={{
                  padding: '6px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-glass)',
                  background: 'transparent',
                  color: 'var(--text-muted)',
                  cursor: 'pointer'
                }}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Drawer Navigation Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border-glass)' }}>
            {[
              { key: 'clips', label: `Clips (${userDetail?.clips?.length || 0})`, icon: <Film size={13} /> },
              { key: 'takes', label: `Takes (${userDetail?.takes?.length || 0})`, icon: <Mic size={13} /> },
              { key: 'entries', label: `Entries (${userDetail?.entries?.length || 0})`, icon: <Layers size={13} /> },
              { key: 'friends', label: `Friends (${userDetail?.friends?.length || 0})`, icon: <Users size={13} /> }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setDetailTab(tab.key as any)}
                style={{
                  flex: 1,
                  padding: '10px 4px',
                  border: 'none',
                  background: detailTab === tab.key ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
                  color: detailTab === tab.key ? 'var(--accent-purple)' : 'var(--text-muted)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px',
                  fontSize: '0.8rem',
                  fontWeight: detailTab === tab.key ? 700 : 500,
                  borderBottom: detailTab === tab.key ? '2px solid var(--accent-purple)' : 'none'
                }}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Drawer Body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem' }}>
            {isLoadingDetail ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                <p>Loading performer assets...</p>
              </div>
            ) : userDetail ? (
              <>
                {/* CLIPS TAB */}
                {detailTab === 'clips' && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        {selectedClipIds.length} clip(s) selected
                      </span>
                      {selectedClipIds.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setIsReassignOpen(true)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            borderRadius: 'var(--radius-sm)',
                            border: 'none',
                            background: 'var(--accent-purple)',
                            color: '#fff',
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          <ArrowRightLeft size={13} />
                          <span>Transfer {selectedClipIds.length} Clips</span>
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'grid', gap: '8px' }}>
                      {userDetail.clips.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                          No clips captured by {selectedUsername}.
                        </div>
                      ) : (
                        userDetail.clips.map(c => {
                          const isSelected = selectedClipIds.includes(c.id);
                          return (
                            <div
                              key={c.id}
                              onClick={() => {
                                setSelectedClipIds(prev =>
                                  isSelected ? prev.filter(id => id !== c.id) : [...prev, c.id]
                                );
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '8px 12px',
                                borderRadius: 'var(--radius-md)',
                                background: isSelected ? 'rgba(139, 92, 246, 0.15)' : 'var(--bg-glass)',
                                border: `1px solid ${isSelected ? 'var(--accent-purple)' : 'var(--border-glass)'}`,
                                cursor: 'pointer'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                style={{ accentColor: 'var(--accent-purple)' }}
                              />
                              <img
                                src={c.poster_url || `/api/media/clips/${c.id}.jpg`}
                                alt={c.title}
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                style={{
                                  width: '44px',
                                  height: '28px',
                                  borderRadius: 'var(--radius-sm)',
                                  objectFit: 'cover'
                                }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: '#fff', fontSize: '0.82rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {c.title}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                                  {c.id} • {Math.round(c.duration || 0)}s
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                {/* TAKES TAB */}
                {detailTab === 'takes' && (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {userDetail.takes.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                        No vocal takes recorded yet.
                      </div>
                    ) : (
                      userDetail.takes.map(t => (
                        <div
                          key={t.id}
                          style={{
                            padding: '10px 14px',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--bg-glass)',
                            border: '1px solid var(--border-glass)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.82rem' }}>
                              Take #{t.take_number} {t.character_label ? `(${t.character_label})` : ''}
                            </span>
                            <span style={{ fontSize: '0.74rem', color: 'var(--accent-coral)', fontWeight: 600 }}>
                              {formatSeconds(t.duration)}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                            Entry: {t.entry_id} • {formatTimestamp(t.created_at)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* ENTRIES TAB */}
                {detailTab === 'entries' && (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {userDetail.entries.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                        No shared entries created yet.
                      </div>
                    ) : (
                      userDetail.entries.map(e => (
                        <div
                          key={e.id}
                          style={{
                            padding: '10px 14px',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--bg-glass)',
                            border: '1px solid var(--border-glass)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.85rem' }}>
                              {e.title}
                            </span>
                            <span style={{
                              fontSize: '0.7rem',
                              padding: '2px 8px',
                              borderRadius: 'var(--radius-full)',
                              background: 'rgba(139, 92, 246, 0.2)',
                              color: 'var(--accent-purple)'
                            }}>
                              {e.mood_tag}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                            {e.take_count} take(s) • Source clip: {e.clip_id}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* FRIENDS TAB */}
                {detailTab === 'friends' && (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {userDetail.friends.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                        No social connections added.
                      </div>
                    ) : (
                      userDetail.friends.map(f => (
                        <div
                          key={f}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 14px',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--bg-glass)',
                            border: '1px solid var(--border-glass)'
                          }}
                        >
                          <Users size={16} color="var(--accent-purple)" />
                          <span style={{ fontWeight: 600, color: '#fff', fontSize: '0.85rem' }}>{f}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* CREATE USER MODAL */}
      {isCreateOpen && (
        <div style={modalOverlayStyle}>
          <div style={modalBoxStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff' }}>Create Performer Profile</h3>
              <button type="button" onClick={() => setIsCreateOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            {createError && (
              <div style={{ padding: '8px 12px', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid var(--accent-coral)', borderRadius: '6px', color: '#fca5a5', fontSize: '0.8rem', marginBottom: '1rem' }}>
                {createError}
              </div>
            )}

            <form onSubmit={handleCreateUser} style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Performer Username *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Dan, Maverick, Sarah"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Email Address (optional)</label>
                <input
                  type="email"
                  placeholder="performer@soulsync.app"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Character Avatar</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                  {['char-maverick', 'char-cyber', 'char-echo', 'char-blaze', 'char-nova'].map(av => (
                    <button
                      type="button"
                      key={av}
                      onClick={() => setNewAvatar(av)}
                      style={{
                        padding: '4px',
                        borderRadius: 'var(--radius-md)',
                        border: newAvatar === av ? '2px solid var(--accent-purple)' : '1px solid var(--border-glass)',
                        background: newAvatar === av ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                        cursor: 'pointer'
                      }}
                    >
                      <CharacterAvatar avatarId={av} size="sm" fallbackInitial="P" />
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setIsCreateOpen(false)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-glass)',
                    background: 'transparent',
                    color: 'var(--text-muted)',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingCreate || !newUsername.trim()}
                  style={{
                    padding: '8px 18px',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    background: 'linear-gradient(135deg, var(--accent-purple) 0%, var(--accent-coral) 100%)',
                    color: 'white',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {isSubmittingCreate ? 'Creating...' : 'Create Performer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {isEditOpen && (
        <div style={modalOverlayStyle}>
          <div style={modalBoxStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff' }}>Edit {selectedUsername}</h3>
              <button type="button" onClick={() => setIsEditOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Email Address</label>
                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Character Avatar</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                  {['char-maverick', 'char-cyber', 'char-echo', 'char-blaze', 'char-nova'].map(av => (
                    <button
                      type="button"
                      key={av}
                      onClick={() => setEditAvatar(av)}
                      style={{
                        padding: '4px',
                        borderRadius: 'var(--radius-md)',
                        border: editAvatar === av ? '2px solid var(--accent-purple)' : '1px solid var(--border-glass)',
                        background: editAvatar === av ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                        cursor: 'pointer'
                      }}
                    >
                      <CharacterAvatar avatarId={av} size="sm" fallbackInitial="P" />
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setIsEditOpen(false)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-glass)',
                    background: 'transparent',
                    color: 'var(--text-muted)',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdateUser}
                  disabled={isSubmittingEdit}
                  style={{
                    padding: '8px 18px',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    background: 'var(--accent-purple)',
                    color: 'white',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {isSubmittingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CHANGE PASSWORD MODAL */}
      {isPasswordOpen && (
        <div style={modalOverlayStyle}>
          <div style={modalBoxStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Key size={18} color="var(--accent-purple)" />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff' }}>
                  Reset Password for {passwordTargetUser}
                </h3>
              </div>
              <button type="button" onClick={() => setIsPasswordOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            {passwordStatusMsg && (
              <div style={{
                padding: '8px 12px',
                background: passwordStatusMsg.isError ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                border: `1px solid ${passwordStatusMsg.isError ? 'var(--accent-coral)' : 'var(--accent-emerald)'}`,
                borderRadius: '6px',
                color: passwordStatusMsg.isError ? '#fca5a5' : 'var(--accent-emerald)',
                fontSize: '0.8rem',
                marginBottom: '1rem'
              }}>
                {passwordStatusMsg.text}
              </div>
            )}

            <form onSubmit={handleChangePassword} style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>New Password</label>
                <input
                  type="text"
                  placeholder="Enter new password (or leave empty to remove password)"
                  value={newPasswordValue}
                  onChange={(e) => setNewPasswordValue(e.target.value)}
                  style={inputStyle}
                  autoFocus
                />
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Updating this changes the password in D1 immediately. Leaving this blank clears the password requirement for this performer.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setIsPasswordOpen(false)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-glass)',
                    background: 'transparent',
                    color: 'var(--text-muted)',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingPassword}
                  style={{
                    padding: '8px 18px',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    background: 'linear-gradient(135deg, var(--accent-purple) 0%, var(--accent-cyan) 100%)',
                    color: 'white',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {isSubmittingPassword ? 'Updating...' : 'Set Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BAN USER MODAL */}
      {isBanOpen && (
        <div style={modalOverlayStyle}>
          <div style={modalBoxStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShieldAlert size={18} color="var(--accent-coral)" />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff' }}>
                  Suspend Performer: {banTargetUser}
                </h3>
              </div>
              <button type="button" onClick={() => setIsBanOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            {banError && (
              <div style={{ padding: '8px 12px', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid var(--accent-coral)', borderRadius: '6px', color: '#fca5a5', fontSize: '0.8rem', marginBottom: '1rem' }}>
                {banError}
              </div>
            )}

            <form onSubmit={handleBanUser} style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Ban Duration</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  {[
                    { label: '7 Days', days: 7 },
                    { label: '30 Days (Standard)', days: 30 },
                    { label: '90 Days', days: 90 },
                    { label: '1 Year (365d)', days: 365 }
                  ].map(preset => (
                    <button
                      type="button"
                      key={preset.days}
                      onClick={() => setBanDays(preset.days)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 'var(--radius-sm)',
                        border: banDays === preset.days ? '1px solid var(--accent-coral)' : '1px solid var(--border-glass)',
                        background: banDays === preset.days ? 'rgba(239, 68, 68, 0.25)' : 'var(--bg-glass)',
                        color: banDays === preset.days ? '#fff' : 'var(--text-secondary)',
                        fontSize: '0.78rem',
                        fontWeight: banDays === preset.days ? 700 : 500,
                        cursor: 'pointer'
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Or custom days:</span>
                  <input
                    type="number"
                    min="1"
                    max="3650"
                    value={banDays}
                    onChange={(e) => setBanDays(Math.max(1, parseInt(e.target.value) || 1))}
                    style={{ ...inputStyle, width: '100px' }}
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Reason for Suspension (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Offensive language, spamming empty takes, policy violation"
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{
                padding: '10px 12px',
                borderRadius: 'var(--radius-sm)',
                background: 'rgba(239, 68, 68, 0.12)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                fontSize: '0.76rem',
                color: '#fca5a5'
              }}>
                <strong>Security Effect:</strong> This will reject all login attempts from {banTargetUser} with HTTP 403 Forbidden until {new Date(Date.now() + (banDays * 86400 * 1000)).toLocaleDateString()}. You can unban at any time.
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setIsBanOpen(false)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-glass)',
                    background: 'transparent',
                    color: 'var(--text-muted)',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingBan}
                  style={{
                    padding: '8px 18px',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    background: 'var(--accent-coral)',
                    color: 'white',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {isSubmittingBan ? 'Applying Ban...' : `Ban for ${banDays} Days`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE USER MODAL */}
      {isDeleteOpen && (
        <div style={modalOverlayStyle}>
          <div style={modalBoxStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={20} color="var(--accent-coral)" />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--accent-coral)' }}>
                  Delete Performer Account
                </h3>
              </div>
              <button type="button" onClick={() => setIsDeleteOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            {deleteError && (
              <div style={{ padding: '8px 12px', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid var(--accent-coral)', borderRadius: '6px', color: '#fca5a5', fontSize: '0.8rem', marginBottom: '1rem' }}>
                {deleteError}
              </div>
            )}

            <form onSubmit={handleDeleteUser} style={{ display: 'grid', gap: '1rem' }}>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                You are about to permanently delete performer <strong>{deleteTargetUser}</strong> from the Cloudflare D1 database.
              </p>

              {/* Asset handling choice */}
              <div style={{ display: 'grid', gap: '8px' }}>
                <label style={labelStyle}>Clips & Media Assets Handling</label>

                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  background: !deletePurgeAssets ? 'rgba(16, 185, 129, 0.12)' : 'var(--bg-glass)',
                  border: !deletePurgeAssets ? '1px solid var(--accent-emerald)' : '1px solid var(--border-glass)',
                  cursor: 'pointer'
                }}>
                  <input
                    type="radio"
                    name="deleteMode"
                    checked={!deletePurgeAssets}
                    onChange={() => setDeletePurgeAssets(false)}
                    style={{ marginTop: '3px', accentColor: 'var(--accent-emerald)' }}
                  />
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff' }}>
                      Orphan Clips (Recommended)
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                      Detaches the performer from all custom clips and takes. Clips stay functional in R2 storage and can be claimed later.
                    </div>
                  </div>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  background: deletePurgeAssets ? 'rgba(239, 68, 68, 0.15)' : 'var(--bg-glass)',
                  border: deletePurgeAssets ? '1px solid var(--accent-coral)' : '1px solid var(--border-glass)',
                  cursor: 'pointer'
                }}>
                  <input
                    type="radio"
                    name="deleteMode"
                    checked={deletePurgeAssets}
                    onChange={() => setDeletePurgeAssets(true)}
                    style={{ marginTop: '3px', accentColor: 'var(--accent-coral)' }}
                  />
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--accent-coral)' }}>
                      Purge All Owned Records
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                      Permanently deletes custom clips, entries, and audio takes created by {deleteTargetUser}.
                    </div>
                  </div>
                </label>
              </div>

              <div>
                <label style={labelStyle}>
                  Type <span style={{ color: '#fff', fontWeight: 800 }}>{deleteTargetUser}</span> to confirm deletion:
                </label>
                <input
                  type="text"
                  placeholder={`Type "${deleteTargetUser}" to confirm`}
                  value={deleteConfirmationText}
                  onChange={(e) => setDeleteConfirmationText(e.target.value)}
                  style={inputStyle}
                  autoFocus
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setIsDeleteOpen(false)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-glass)',
                    background: 'transparent',
                    color: 'var(--text-muted)',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingDelete || deleteConfirmationText.trim().toLowerCase() !== (deleteTargetUser || '').toLowerCase()}
                  style={{
                    padding: '8px 18px',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    background: deleteConfirmationText.trim().toLowerCase() === (deleteTargetUser || '').toLowerCase()
                      ? 'var(--accent-coral)'
                      : 'rgba(239, 68, 68, 0.3)',
                    color: 'white',
                    fontWeight: 700,
                    cursor: deleteConfirmationText.trim().toLowerCase() === (deleteTargetUser || '').toLowerCase() ? 'pointer' : 'not-allowed'
                  }}
                >
                  {isSubmittingDelete ? 'Deleting...' : 'Permanently Delete'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REASSIGN CLIPS MODAL */}
      {isReassignOpen && (
        <div style={modalOverlayStyle}>
          <div style={modalBoxStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff' }}>
                Transfer {selectedClipIds.length} Clip(s)
              </h3>
              <button type="button" onClick={() => setIsReassignOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              Transfer ownership of {selectedClipIds.length} selected clips from <strong>{selectedUsername}</strong> to another performer:
            </p>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Target Performer Username *</label>
              <input
                type="text"
                placeholder="Enter target username..."
                value={targetUsername}
                onChange={(e) => setTargetUsername(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setIsReassignOpen(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-glass)',
                  background: 'transparent',
                  color: 'var(--text-muted)',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReassignClips}
                disabled={isReassigning || !targetUsername.trim()}
                style={{
                  padding: '8px 18px',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: 'linear-gradient(135deg, var(--accent-emerald) 0%, var(--accent-cyan) 100%)',
                  color: 'white',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {isReassigning ? 'Transferring...' : 'Confirm Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const cardStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--bg-card)',
  border: '1px solid var(--border-glass)',
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
};

const metricValueStyle: React.CSSProperties = {
  fontSize: '1.4rem',
  fontWeight: 800,
  color: 'var(--text-primary)',
  fontFamily: 'var(--font-display)',
  lineHeight: 1.1
};

const metricLabelStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginTop: '2px'
};

const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  color: 'var(--text-muted)',
  fontWeight: 600,
  fontSize: '0.74rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px'
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.75)',
  backdropFilter: 'blur(6px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: '1rem'
};

const modalBoxStyle: React.CSSProperties = {
  width: '460px',
  maxWidth: '94vw',
  background: 'var(--bg-deep)',
  border: '1px solid var(--border-glow)',
  borderRadius: 'var(--radius-lg)',
  padding: '1.5rem',
  boxShadow: 'var(--shadow-lg)'
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: '6px'
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border-glass)',
  background: 'var(--bg-glass)',
  color: '#fff',
  fontFamily: 'var(--font-body)',
  fontSize: '0.85rem',
  outline: 'none'
};
